import { useState, useContext } from 'react';
import * as Yup from 'yup';
import { observer } from 'mobx-react';
import { Formik } from 'formik';
import { IconButton } from 'uiKit/atoms/IconButton/IconButton';
import { InputAdornment } from 'uiKit/atoms/InputAdornment/InputAdornment';
import { CloseOutlined } from 'uiKit/atoms/CloseOutlined/CloseOutlined';
import { PrimaryButton } from 'uiKit/atoms/Button/components/PrimaryButton';
import { ReactComponent as ContactUsMailSvg } from '../../../assets/img/contact-us-mail.svg';
import { getContactUsData } from '../../../helpers/utils';
import { RequiredFieldString } from '../../../constants';
import { useStores } from 'stores';
import { AuthContext } from 'context/AuthContext';
import styles from './ContactUs.module.scss';
import { TextField } from 'uiKit/atoms/TextField/TextField';

const ContactUsSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email')
    .min(3)
    .max(255, 'The length of Email must be 255 characters or fewer.')
    .required(RequiredFieldString),
  name: Yup.string()
    .min(2)
    .max(255, 'The length of name must be 255 characters or fewer.')
    .required(RequiredFieldString),
  feedbackComment: Yup.string().min(2, 'Feedback Comment must be at least 2 characters').required(RequiredFieldString),
});

const ContactUs = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const { userStore } = useStores();
  const [showPanel, setShowPanel] = useState(false);

  const contact = () => setShowPanel(true);
  const close = () => setShowPanel(false);

  const getDataProps = {
    isAuthenticated,
    email: userStore.userProfile.email,
    firstName: userStore.userProfile.firstName,
  };

  return (
    <div>
      {!showPanel && (
        <div className={styles.bns3_contact_us_icon_container} onClick={contact}>
          <div className={styles.bns3_contact_us_icon}>
            <ContactUsMailSvg />
          </div>
        </div>
      )}

      {showPanel && (
        <Formik
          initialValues={getContactUsData(getDataProps)}
          enableReinitialize
          validationSchema={ContactUsSchema}
          onSubmit={values => {
            const data = {
              name: values.name,
              email: values.email,
              message: values.feedbackComment,
            };
            setShowPanel(false);
            userStore.contactUs(data);
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <div className={styles.bns3_contact_us_form}>
                <div className={styles.bns3_contact_us_formHeading}>
                  <div>Send us your feedback!</div>
                  <IconButton onClick={close} className={styles.bns3_contact_us_closeBtn}>
                    <CloseOutlined />
                  </IconButton>
                </div>

                <div className={styles.bns3_contact_us_formField}>
                  <TextField
                    fullWidth
                    id="contact-us-email"
                    name="email"
                    label="Email"
                    placeholder="Enter your email..."
                    variant="outlined"
                    size="small"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && Boolean(errors.email)}
                  />
                </div>

                <div className={styles.bns3_contact_us_formField}>
                  <TextField
                    fullWidth
                    id="contact-us-name"
                    name="name"
                    label="Name"
                    placeholder="Enter your name..."
                    variant="outlined"
                    size="small"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && Boolean(errors.name)}
                  />
                </div>

                <div className={styles.bns3_contact_us_formField}>
                  <TextField
                    fullWidth
                    id="feedbackComment"
                    name="feedbackComment"
                    label="Comments"
                    placeholder="Please share your feedback. The more detail, the better."
                    variant="outlined"
                    size="small"
                    multiline
                    minRows={4}
                    maxRows={6}
                    inputProps={{ maxLength: 255 }}
                    value={values.feedbackComment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.feedbackComment && Boolean(errors.feedbackComment)}
                    helperText={touched.feedbackComment && errors.feedbackComment}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{ position: 'absolute', bottom: 16, left: 8 }}
                        >
                          <span className={styles.bns3_text_counter}>
                            {values.feedbackComment.length}/255
                          </span>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { paddingBottom: '28px' } }}
                  />
                </div>

                <div className={styles.bns3_button_flex_container}>
                  <PrimaryButton type="submit" className={styles.bns3_contact_us_submitBtn}>
                    <span>Submit</span>
                  </PrimaryButton>
                </div>
              </div>
            </form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default observer(ContactUs);
